import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ShieldCheck, Download, RotateCcw, ArrowRight } from "lucide-react";
import { CHECKOUT_URL, PRICE_ORIGINAL, PRICE_DISCOUNTED, BOOK_COVER } from "@/lib/constants";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 140, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 140, damping: 18 });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="topo"
      data-testid="hero-section"
      ref={sectionRef}
      onMouseMove={handleMouse}
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
    >
      <motion.div style={{ y: glowY }} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full bg-[#d4af37]/[0.07] blur-[140px]" />
        <div className="absolute bottom-[-30%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#997a15]/[0.08] blur-[120px]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center w-full">
        <div className="lg:col-span-7">
          <MaskedLine delay={0.15}>
            <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#d4af37]">
              Um guia prático de filosofia estoica · LF Silva
            </span>
          </MaskedLine>

          <h1 className="mt-7 font-serif font-bold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[4.4rem] text-[#f4f1ea]">
            <MaskedLine delay={0.3}>A mente inabalável</MaskedLine>
            <MaskedLine delay={0.42}>não nasce pronta.</MaskedLine>
            <MaskedLine delay={0.54} className="text-gold-gradient italic">Ela se constrói.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
          >
            <span className="text-[#f4f1ea] font-semibold">ESTÓICO</span> é o guia prático para aplicar a
            filosofia antiga de Sêneca, Epicteto e Marco Aurélio no caos do século XXI — e transformar
            ansiedade em serenidade, reação em resposta, ruído em clareza.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="mt-9 flex flex-wrap items-center gap-6"
          >
            <a
              data-testid="hero-buy-button"
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-gold-gradient text-[#08080a] font-extrabold text-sm sm:text-base px-8 py-4 rounded-full gold-glow transition-transform duration-300 hover:scale-[1.04]"
            >
              Quero meu exemplar — {PRICE_DISCOUNTED}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <div className="flex items-baseline gap-3">
              <span data-testid="hero-price-original" className="text-zinc-500 line-through text-lg">{PRICE_ORIGINAL}</span>
              <span data-testid="hero-price-discounted" className="font-serif text-4xl font-bold text-gold-gradient">{PRICE_DISCOUNTED}</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-8 flex flex-wrap gap-x-7 gap-y-3 text-xs sm:text-sm text-zinc-500"
          >
            <span className="flex items-center gap-2"><Download className="w-4 h-4 text-[#d4af37]" /> Download imediato</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#d4af37]" /> Pagamento seguro</span>
            <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-[#d4af37]" /> 7 dias de garantia</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end" style={{ perspective: 1400 }}>
          <motion.div
            initial={{ opacity: 0, y: 60, rotateY: -18 }}
            animate={{ opacity: 1, y: 0, rotateY: 0 }}
            transition={{ duration: 1.3, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              data-testid="hero-book-cover"
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative animate-float-slow"
            >
              <div className="absolute -inset-10 bg-[#d4af37]/15 blur-[70px] rounded-full" style={{ transform: "translateZ(-80px)" }} />
              <img
                src={BOOK_COVER}
                alt="Capa do e-book Estóico, de LF Silva"
                className="relative w-[300px] sm:w-[360px] rounded-md shadow-[0_50px_100px_-20px_rgba(0,0,0,0.9)] ring-1 ring-[#d4af37]/30"
              />
              <div className="absolute inset-0 rounded-md bg-gradient-to-tr from-transparent via-white/[0.06] to-white/[0.14] pointer-events-none" />
              <div
                className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#1f1a08] border border-[#d4af37]/40 rounded-full px-5 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#f5d77f]"
                style={{ transform: "translateZ(50px)" }}
              >
                E-book digital · 37% off
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
